import React, { useContext, useEffect, useState } from 'react';
import { SubscriptionPlan } from '@dayetopia/types';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { TypeOfPurchase } from '@components/shared/PurchaseOptions';
import OptionBoxes from '@components/shared/subscription/OptionBoxes';
import OptionRows from '@components/shared/subscription/OptionRows';
import { P } from '@components/shared/Typography';
import { ProductCode } from '@contracts';
import IconArrow from '@images/arrow.svg';
import RepeatIcon from '@images/multi-month/icon-lg-repeat.svg';
import SelectedRadio from '@images/selected-radio.svg';
import UnselectedRadio from '@images/unselected-radio.svg';
import { getDiscountPercent } from '@utils/subscriptions';

interface Props {
  showRadioButton: boolean;
  selected: boolean;
  setSelected: (value: TypeOfPurchase) => void;
  setSelectedProductCallback: (subscriptionPlan: SubscriptionPlan) => void;
  productCode: ProductCode;
  subscriptions: SubscriptionPlan[];
  defaultSubscription: SubscriptionPlan;
  optionsDescription?: string;
  layout?: string;
  textColor?: string;
}

const MultiMonthOptions: React.FC<Props> = ({
  showRadioButton,
  selected,
  setSelected,
  setSelectedProductCallback,
  productCode,
  subscriptions,
  defaultSubscription,
  optionsDescription,
  layout = '0',
  textColor = Colors.navyBlue
}) => {
  const { t } = useTranslation();
  const [selectedSubscription, setSelectedSubscription] = useState(defaultSubscription);
  const { state } = useContext(Context);
  const { currency, country } = state;
  const [isOpen, setIsOpen] = useState(false);
  const maxDiscount = Math.round(subscriptions.reduce((max: number, subscription) => Math.max(max, getDiscountPercent(subscription, country)), 0));
  const multiMonthEligible = [ProductCode.Pads, ProductCode.Proviotics];

  useEffect(() => {
    setIsOpen(false);
  }, [selected]);

  useEffect(() => {
    setSelectedSubscription(defaultSubscription);
  }, [defaultSubscription]);

  const onOptionSelect = (subscription: SubscriptionPlan) => {
    setSelected(true);
    setSelectedSubscription(subscription);
    setSelectedProductCallback(subscription);
    setIsOpen(false);
  };

  if (layout === '1' || multiMonthEligible.includes(productCode)) {
    return (
      <OptionRows
        subscriptions={subscriptions}
        productCode={productCode}
        selectedSubscription={selected ? selectedSubscription : null}
        onOptionSelect={onOptionSelect}
        textColor={textColor}
      />
    );
  }

  return (
    <Container $isSelected={selected} $textColor={textColor}>
      {/* A/B options also have a different Headliner - one has the price per pack of the selected item, the other one only has the discount */}
      <OptionHeadliner
        $isSelected={selected}
        onClick={() => {
          setSelected(TypeOfPurchase.SubscriptionPurchase);
          onOptionSelect(selectedSubscription);
        }}
      >
        <input hidden type="radio" value={TypeOfPurchase.SubscriptionPurchase} />
        {showRadioButton && <RadioIndicator $textColor={textColor}>{selected ? <SelectedRadio /> : <UnselectedRadio />}</RadioIndicator>}
        <PurchaseOptionTextWrap>
          <PurchaseDescription $textColor={textColor}>{t('components.subscription-options.subscribe')}</PurchaseDescription>
          <SavePercentage>{t('components.subscription-options.save-up-to', { percent: maxDiscount })}</SavePercentage>
        </PurchaseOptionTextWrap>
      </OptionHeadliner>
      {selected && (
        <OptionsWrap>
          {layout === '0' ? (
            <OptionBoxes
              subscriptions={subscriptions}
              productCode={productCode}
              selectedSubscription={selectedSubscription}
              onOptionSelect={onOptionSelect}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <OptionsDropdown onClick={() => setIsOpen(!isOpen)} $isSelected={isOpen}>
                <RepeatIcon />
                <div style={{ marginLeft: '20px', marginRight: 'auto' }}>
                  <TopText>
                    {t(
                      selectedSubscription.subscriptionInterval.length > 1
                        ? 'components.subscription-options.dropdown.monthly-multi-delivery'
                        : 'components.subscription-options.dropdown.monthly-single-delivery',
                      { length: selectedSubscription.subscriptionInterval.length }
                    )}
                  </TopText>
                  {selectedSubscription.subscriptionInterval.length !== selectedSubscription.billing.installments && (
                    <BottomText>
                      <Trans
                        i18nKey={
                          selectedSubscription.billing.installments > 1
                            ? 'components.subscription-options.pay-multiple-installments'
                            : 'components.subscription-options.pay-single-installment'
                        }
                        values={{
                          installments: selectedSubscription.billing.installments,
                          currency: currency.symbol,
                          installmentPrice: selectedSubscription.billing.installmentPrice?.toFixed(2)
                        }}
                      />
                    </BottomText>
                  )}
                </div>
                <IconArrowStyled $isOpen={isOpen} />
              </OptionsDropdown>
              <OptionsDropdownItems $isOpen={isOpen}>
                {subscriptions.map((subscription) => (
                  <OptionHeadliner
                    $height="86px"
                    $isSelected={selectedSubscription.planCode === subscription.planCode}
                    key={subscription.planCode}
                    onClick={() => {
                      onOptionSelect(subscription);
                    }}
                  >
                    <input hidden type="radio" value={TypeOfPurchase.SubscriptionPurchase} />
                    <RadioIndicator $textColor={textColor}>
                      {selectedSubscription.planCode === subscription.planCode ? <SelectedRadio /> : <UnselectedRadio />}
                    </RadioIndicator>
                    <PurchaseOptionTextWrap>
                      <PurchaseDescription $textColor={textColor}>
                        <div>
                          {t(
                            subscription.subscriptionInterval.length > 1
                              ? 'components.subscription-options.dropdown.delivery-multi-month'
                              : 'components.subscription-options.dropdown.delivery-single-month',
                            { length: subscription.subscriptionInterval.length }
                          )}
                        </div>
                        {subscription.subscriptionInterval.length !== subscription.billing.installments && (
                          <OptionDescription>
                            <Trans
                              i18nKey={
                                subscription.billing.installments > 1
                                  ? 'components.subscription-options.pay-multiple-installments'
                                  : 'components.subscription-options.pay-single-installment'
                              }
                              values={{
                                installments: subscription.billing.installments,
                                currency: currency.symbol,
                                installmentPrice: subscription.billing.installmentPrice?.toFixed(2)
                              }}
                            />
                          </OptionDescription>
                        )}
                        <DiscountBox>
                          {t('components.subscription-options.save', {
                            percent: Math.round(getDiscountPercent(subscription, country))
                          })}
                        </DiscountBox>
                        {subscription.freeItem && (
                          <FreeItemBox className={`key-${subscription.freeItem.key}`}>
                            {t(`components.subscription-options.${subscription.freeItem.key}`)}
                          </FreeItemBox>
                        )}
                      </PurchaseDescription>
                      <OptionsPrice color="transparent">
                        <div style={{ height: '30px' }}>
                          {currency.symbol}
                          {subscription.discountedPrice?.toFixed(2)}
                        </div>
                        <Span>{t('components.subscription-options.per-pack')}</Span>
                      </OptionsPrice>
                    </PurchaseOptionTextWrap>
                  </OptionHeadliner>
                ))}
              </OptionsDropdownItems>
            </div>
          )}
          {optionsDescription && <OptionsDescription dangerouslySetInnerHTML={{ __html: optionsDescription }} />}
        </OptionsWrap>
      )}
    </Container>
  );
};

export default MultiMonthOptions;

interface StyleProps {
  $isSelected?: boolean;
  $selectedOption?: boolean;
  $isOpen?: boolean;
  $isHidden?: boolean;
  $height?: string;
  $textColor?: string;
  $layout?: string;
}

const Container = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border-style: solid;
  border-width: ${({ $isSelected }) => ($isSelected ? '2px' : '1px')};
  border-color: ${Colors.gold};
  background-color: ${Colors.cream};
  color: ${({ $textColor }) => $textColor};

  svg .color-stroke {
    stroke: ${(props) => props.$textColor ?? Colors.forestGreen};
  }

  svg .color-fill {
    fill: ${(props) => props.$textColor ?? Colors.forestGreen};
  }
`;

const OptionsDropdown = styled.div<StyleProps>`
  display: flex;
  padding: 0 30px 15px 20px;
  align-items: center;
  border-bottom: ${({ $isSelected }) => ($isSelected ? `2px solid ${Colors.gold}` : `1px solid #FAEAD6`)};
  p {
    color: inherit;
  }
`;

const TopText = styled.p`
  font-family: BrownStd;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  text-align: left;
  line-height: 120%;
  margin-bottom: 5px;
`;

const BottomText = styled.p`
  font-family: BrownStd;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0;
  text-align: left;
`;

const OptionsDropdownItems = styled.div<StyleProps>`
  box-sizing: border-box;
  width: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  background: ${Colors.cream};
  height: 0;
  transition: height 0.2s ease-out;
  z-index: 2;
  ${({ $isOpen }) => $isOpen && `height: 344px`};
`;

const OptionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px 0 0;
  justify-content: center;

  @media (max-width: 1024px) {
    gap: 5px;
  }
`;

const OptionsDescription = styled(P)`
  margin-bottom: 15px;
  text-align: center;
  font-weight: 700;
  font-size: 12px;
  line-height: 150%;
  letter-spacing: 0;
  text-align: center;
  color: inherit;
`;

const OptionHeadliner = styled.div<StyleProps>`
  display: flex;
  height: ${({ $height }) => $height || '66px'};
  transition: background 0.2s;
  border-radius: ${({ $isSelected }) => ($isSelected ? '12px 12px 0 0' : '12px')};
  border-bottom: 1px solid #faead6;
  cursor: ${({ $isSelected }) => ($isSelected ? 'default' : 'pointer')};
  background-color: ${({ $isSelected }) => ($isSelected ? `${Colors.ecru}80` : Colors.cream)};

  &:last-of-type {
    border: none;
  }
`;

const PurchaseDescription = styled.p<StyleProps>`
  font-family: BrownStd;
  font-size: 16px;
  line-height: 26px;
  font-style: normal;
  font-weight: 700;
  text-align: left;
  color: ${(props) => props.$textColor};

  @media (max-width: 1024px) {
    font-size: 16px;
    line-height: 26px;
  }
`;

const SavePercentage = styled(P)`
  font-style: normal;
  font-weight: 700;
  font-size: 15px;
  line-height: 100%;
  text-transform: uppercase;
  text-align: right;
  letter-spacing: 0.5px;
  color: ${Colors.lilac};

  @media (max-width: 1024px) {
    font-size: 14px;
  }
`;

const PurchaseOptionTextWrap = styled.div`
  width: 100%;
  margin: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OptionsPrice = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: 'Domaine';
  font-size: 20px;
  font-style: normal;
  font-weight: normal;
  letter-spacing: 0;
  height: 50px;
  line-height: 50px;
  text-align: center;
  padding-right: 18px;
`;

const RadioIndicator = styled.div<StyleProps>`
  margin-left: 20px;
  align-self: center;
  width: 20px;
  height: 20px;

  svg circle:first-child {
    stroke: ${({ $textColor }) => $textColor};
    fill: #0000 !important;
  }
  svg circle:last-child {
    fill: ${({ $textColor }) => $textColor};
  }
`;

const Span = styled.span`
  opacity: 0.5;
  color: inherit;
  font-family: BrownStd;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
  letter-spacing: 0;
  text-align: right;
  white-space: nowrap;
`;

const IconArrowStyled = styled(IconArrow)<{ $isOpen: boolean }>`
  width: 17px;
  height: 11px;
  transition: transform 0.2s ease-out;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : null)};
`;

const DiscountBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.lilac};
  color: ${Colors.cream};
  border-radius: 2px;
  margin-top: 3px;
  padding: 2px 4px 1px;
  display: inline-block;
  text-transform: uppercase;

  font-family: BrownStd;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 11px;
  letter-spacing: 0.5px;
`;

const FreeItemBox = styled(P)`
  font-weight: 700;
  font-size: 10px;
  line-height: 100%;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  &.key-flexible {
    display: none;
  }
`;

const OptionDescription = styled.div`
  font-family: BrownStd;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0;
  text-align: left;
`;
